import { applySnapshot, flow, Instance, types } from 'mobx-state-tree';
import { FieldState, FormState, Validator } from 'formstate';
import { getFormValues } from '@yl/react-forms/dist';
import { AddressEntryFormData } from './AddressEntryFormData';
import { AddressModuleForm } from './module-form/AddressModuleForm';
import { AddressRow } from './module-form/AddressRow';
import { PostalCodeValidation } from './PostalCodeValidation';
import { maxByteSize } from './validators/max-byte-size';
import { matchesRegExp } from '../../customer-information/shared/validators/RegularExpressionValidator';
import { AddressValidator } from '../validator/AddressValidator';
import { required } from '../../customer-information/shared/validators/RequiredValidator';
import { PhoneValidation } from '../../customer-information/phone/PhoneValidation';
import { Suburb } from '../../../reference/Suburb';
import { replaceUnallowedCharacters } from '../../../infrastructure/forms/InputTextFormatter';
import { ComponentField } from '../../../infrastructure/dynamic-forms/ComponentField';
import { City } from '../../../reference/City';
import { postalCodeFormatter } from '../../../infrastructure/forms/postalCodeFormatter';
import utf8 from 'utf8';

export const AddressEntryStore = types
  .model({
    form: AddressModuleForm,
    data: AddressEntryFormData,
    fieldRegularExpression: types.string,
    postalCodeValidation: types.maybe(PostalCodeValidation),
    phoneValidation: types.maybe(PhoneValidation),
    storeId: types.number,
    shouldValidateAddress: types.boolean,
    addressValidator: types.optional(AddressValidator, {}),
    showMarketChangeWarning: false,
    invalidPostalCodeMessage: '',
    lastPostalCode: '',
    inEditMode: types.boolean,
    shouldConfirmAddress: types.boolean,
    targetCountry: types.maybe(types.string),
    targetStateProv: types.maybe(types.string),
    targetCity: types.maybe(types.string),
    jpZipNotFound: false
  })
  .actions(self => ({
    updateFromForm(data: any) {
      if (self.data === undefined) {
        self.data = AddressEntryFormData.create();
      }

      self.data.street1 = data.street1;
      self.data.street2 = data.street2;
      self.data.street3 = data.street3;
      self.data.city = data.city;
      self.data.stateProv = data.stateProv;
      self.data.postalCode = data.postalCode;
      self.data.country = data.country;
      self.jpZipNotFound = data.jpZipNotFound;
      //optional fields
      if (data.neighborhood) {
        self.data.neighborhood = data.neighborhood;
      }
      if (data.houseNumber) {
        self.data.houseNumber = data.houseNumber;
      }
      if (data.interiorNumber) {
        self.data.interiorNumber = data.interiorNumber;
      }
    },
    disableFormFieldsIfNeeded(data: any) {
      self.form.rows.forEach((row: AddressRow) => {
        row.formFields.forEach(field => {
          if (data.city && field.name === 'city') {
            // Only disable the city field if the value is valid (not exceeding maxLength)
            // Use the city value from self.data (which was updated by updateFromForm) or fallback to data.city
            const cityValue = (self.data && self.data.city) || data.city || '';
            // Only check maxLength for Input fields (Select fields don't have maxLength validation)
            const isInputField = field.fieldType === 'Input';
            // Check if the city value exceeds the maxLength constraint
            // If maxLength is defined and city value exceeds it, keep field enabled for editing
            const exceedsMaxLength = isInputField && field.maxLength && cityValue && utf8.encode(cityValue).length > field.maxLength;
            // If city exceeds maxLength, enable the field (disabled = false) so user can edit it
            // If city is valid or not an Input field, disable the field (disabled = true) as before
            field.disabled = !exceedsMaxLength;
          }
          if (data.state && field.name === 'stateProv') {
            field.disabled = true;
          }
        });
      });
    },
    
    updatePostalCodeValidationMessage(data: any) {
      const isPostalCodeValid = data.city || data.state || data.municipality;

      if (isPostalCodeValid) {
        self.invalidPostalCodeMessage = "";
      }
      else {
        self.invalidPostalCodeMessage = "Invalid postal code";
      }
    }
  }))
  .views(self => ({
    get supportsDisplay(): boolean {
      return true;
    },
    get reactForm() {
      const forms = {} as any;
      const formValues = self.data as any;
      self.form.rows.forEach((row: AddressRow) => {
        row.formFields.forEach(field => {
          if (field.name != "phoneNumber") {
            const fieldState = new FieldState(formValues[field.name]);
            const validators: Validator<any>[] = [];
            if (field.required) {
              validators.push(required());
            }
            if (field.fieldType === 'Input') {
              validators.push(maxByteSize(field.maxLength));
              validators.push(matchesRegExp(self.fieldRegularExpression, 'InvalidAddress'));
            }

            if (field.name === 'postalCode' && self.postalCodeValidation) {
              validators.push(matchesRegExp(self.postalCodeValidation.validRegEx, 'VerifyPostalcodePatternError'));
              validators.push(matchesRegExp(self.postalCodeValidation.allowedCharactersRegEx, 'InvalidZipCode'));
            }

            fieldState.validators(...validators);
            forms[field.name] = fieldState;
          } 
        });
      });

      return new FormState(forms);
    },
    get isFormDirty() {
      return Object.values(this.reactForm.$ as FieldState<any>).some(x => x.dirty);
    },
    getDataToSubmit() {
      const toSubmit = {
        ...getFormValues(this.reactForm),
        isValidated: self.data.isValidated
      };
      self.updateFromForm(toSubmit);

      return toSubmit;
    },

    canProceed() {
      return !self.inEditMode || !this.reactForm.hasError;
    },

    setAllowCustomSuburb(noSuburb: boolean) {
      self.form.rows.forEach((row: AddressRow) => {
        row.formFields.forEach(field => {
          if (field.name === 'stateProv') {
            field.required = !noSuburb;
          }
          if (field.name === 'suburb') {
            field.required = !noSuburb;
          }
          if (field.name === 'postalCode') {
            field.disabled = !noSuburb;
          }
        });
      });
    }
  }))
  .actions(self => ({
    validateForm() {
      if (!self.inEditMode) {
        return Promise.resolve(true);
      }

      if (self.isFormDirty) {
        self.data.isValidated = false;
      }
      return self.reactForm.validate();
    },
    applyFormat: flow(function* debounceFormat(field: ComponentField) {
      yield new Promise(r => setTimeout(r, 1000));
      replaceUnallowedCharacters(self.reactForm.$[field.name], field.formatRegexForReplace);
    }),
    applyFormatByCountry: flow(function* debounceFormat(field: ComponentField, countryIso: string) {
      if (field.name === 'postalCode') {
        postalCodeFormatter(self.reactForm.$[field.name], countryIso);
      }

      yield new Promise(r => setTimeout(r, 1000));
      replaceUnallowedCharacters(self.reactForm.$[field.name], field.formatRegexForReplace);

    }),
    warnAboutMarketChange(country: string) {
      self.showMarketChangeWarning = true;
      self.targetCountry = country;
    },
    setStateProv(stateProv: string) {
      self.targetStateProv = stateProv;
    },
    setCity(city: string) {
      self.targetCity = city;
    },
    setPostalCode(postalCode: string){
      self.lastPostalCode = postalCode;
    },
    UpdateSuburb(suburb: Suburb) {
      self.reactForm.$.postalCode.value = suburb.postalCode;
    },
    UpdateCity(city: City) {
      self.reactForm.$.city.values = city.name;
    },
    UpdateNoSuburb(noSuburb: boolean) {
      self.data.stateProv = self.reactForm.$.stateProv.value;
      self.data.noSuburb = noSuburb;
      if (noSuburb === true) {
        self.reactForm.$.suburb.value = null;
        self.reactForm.$.postalCode.value = null;
      }
      self.setAllowCustomSuburb(noSuburb);
    },
    setInEditMode() {
      self.inEditMode = true;
    },
    cancelMarketChange() {
      self.reactForm.$.country.value = self.data.country!;
      self.reactForm.$.country.$ = self.data.country!;
      self.showMarketChangeWarning = false;
    },
    prepareResults: flow(function* prepareResults() {
      if (!self.shouldValidateAddress || !self.isFormDirty) {
        return true;
      }
      const selectedAddress = yield self.addressValidator.validateAddress(self.getDataToSubmit());

      if (!selectedAddress) {
        return false;
      }
      applySnapshot(self.data, selectedAddress);
      return true;
    })
  }));

export type AddressEntryStore = Instance<typeof AddressEntryStore>;
